import { useState } from "react";
import { kidStyle } from "../kid-style";
import type { Kid } from "../../shared/types";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

/** Where a kid's photo is served. The version in the URL makes each new photo a new URL. */
export function kidPhotoUrl(kidId: string, version: number): string {
  return `/api/kids/${encodeURIComponent(kidId)}/photo?v=${version}`;
}

/**
 * A kid as a circle in their colour's ring: their photo when they have one, else their initial
 * on their colour. Decorative: the kid's name is always written next to it.
 */
export function KidAvatar({
  kid,
  size,
}: {
  kid: Pick<Kid, "id" | "name" | "colour" | "photoUpdatedAt">;
  size: AvatarSize;
}) {
  const src = kid.photoUpdatedAt === null ? null : kidPhotoUrl(kid.id, kid.photoUpdatedAt);
  // The URL that failed to load (offline before it was cached, removed on another phone), so a
  // newer photo is still tried.
  const [failed, setFailed] = useState<string | null>(null);
  const showPhoto = src !== null && src !== failed;

  return (
    <span className={`avatar avatar--${size}`} style={kidStyle(kid.colour)} aria-hidden="true">
      {showPhoto ? (
        <img className="avatar__img" src={src} alt="" decoding="async" onError={() => setFailed(src)} />
      ) : (
        <span className="avatar__initial">{initial(kid.name)}</span>
      )}
    </span>
  );
}

/** The first letter of the name, whole even when it is an emoji or an accented letter. */
function initial(name: string): string {
  return (Array.from(name.trim())[0] ?? "?").toUpperCase();
}
